import React from 'react';

export const PAGE_W = 595;
export const PAGE_H = 842;
const M   = 40;   // side margin
const HDR = 34;   // header strip height
const FTR = 28;   // footer strip height
const CW  = PAGE_W - M * 2; // usable content width

// ─── Theme helper ────────────────────────────────────────────────────────────
function hexRgb(hex = '#6B5744') {
  const h = hex.replace('#', '');
  return {
    r: parseInt(h.slice(0, 2), 16) || 107,
    g: parseInt(h.slice(2, 4), 16) || 87,
    b: parseInt(h.slice(4, 6), 16) || 68,
  };
}

function T(branding = {}) {
  const accent = branding?.accent_color || '#6B5744';
  const { r, g, b } = hexRgb(accent);
  const fs = branding?.font_style || 'minimal';
  const fm = {
    minimal:   { h: "Georgia,'Times New Roman',serif",   bdy: "system-ui,-apple-system,'Helvetica Neue',Arial,sans-serif" },
    nursery:   { h: "'Trebuchet MS',Georgia,serif",       bdy: "'Trebuchet MS',Arial,sans-serif" },
    botanical: { h: "Georgia,'Palatino Linotype',serif",  bdy: "Georgia,serif" },
    classic:   { h: "Georgia,'Times New Roman',serif",    bdy: "'Times New Roman',Georgia,serif" },
  };
  const f = fm[fs] || fm.minimal;
  return {
    a:   accent,
    aL:  `rgba(${r},${g},${b},0.07)`,
    aM:  `rgba(${r},${g},${b},0.17)`,
    aB:  `rgba(${r},${g},${b},0.35)`,
    h:   f.h,
    bdy: f.bdy,
    mo:  "'Courier New',Courier,monospace",
  };
}

// ─── Shared shells ────────────────────────────────────────────────────────────
function Pg({ t, children, bg = '#ffffff' }) {
  return (
    <div style={{
      width: PAGE_W, height: PAGE_H, background: bg,
      position: 'relative', overflow: 'hidden', flexShrink: 0,
      fontFamily: t.bdy,
    }}>
      {children}
    </div>
  );
}

function Hdr({ t, title, shop, pg }) {
  return (
    <div style={{
      position: 'absolute', top: 0, left: 0, right: 0, height: HDR,
      background: t.aL, borderBottom: `2px solid ${t.aM}`,
      display: 'flex', alignItems: 'center', padding: `0 ${M}px`,
      boxSizing: 'border-box',
    }}>
      <span style={{ fontSize: 8, fontWeight: 700, color: t.a, textTransform: 'uppercase', letterSpacing: '0.14em', fontFamily: t.bdy, flex: 1 }}>
        {title || 'Crochet Pattern'}
      </span>
      {shop && <span style={{ fontSize: 8, color: '#9CA3AF', fontFamily: t.bdy, marginRight: 14 }}>{shop}</span>}
      <span style={{ fontSize: 8, fontWeight: 700, color: t.a, fontFamily: t.bdy }}>{pg}</span>
    </div>
  );
}

function Ftr({ t, copy, shop, ig }) {
  return (
    <div style={{
      position: 'absolute', bottom: 0, left: 0, right: 0, height: FTR,
      background: t.aL, borderTop: `1px solid ${t.aM}`,
      display: 'flex', alignItems: 'center', padding: `0 ${M}px`,
      boxSizing: 'border-box',
    }}>
      <span style={{ fontSize: 7.5, color: '#B0B8C1', fontFamily: t.bdy, flex: 1 }}>{copy || ''}</span>
      <span style={{ fontSize: 7.5, color: '#9CA3AF', fontFamily: t.bdy }}>
        {[shop, ig ? `@${ig}` : null].filter(Boolean).join(' · ')}
      </span>
    </div>
  );
}

// Content area: sits between header and footer with padding
function Body({ children }) {
  return (
    <div style={{
      position: 'absolute',
      top: HDR, bottom: FTR, left: M, right: M,
      overflow: 'hidden',
      paddingTop: 22,
      boxSizing: 'border-box',
    }}>
      {children}
    </div>
  );
}

function SecTitle({ t, label }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827', fontFamily: t.h, lineHeight: 1.2, margin: '0 0 7px 0' }}>
        {label}
      </h2>
      <div style={{ height: 3, width: 38, background: t.a, borderRadius: 2 }} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PAGE 1 — COVER
// ─────────────────────────────────────────────────────────────────────────────
export function CoverPage({ pattern, photos, branding }) {
  const t   = T(branding);
  const img = (photos || []).find(p => p.category === 'cover');

  return (
    <Pg t={t} bg="#FAF7F4">
      {/* Background */}
      {img ? (
        <img src={img.url} alt="" crossOrigin="anonymous"
             style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        <div style={{
          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
          background: `linear-gradient(150deg,#FAF7F4 0%,#EDE5DB 45%,${t.aM} 100%)`,
        }}>
          <div style={{ position: 'absolute', bottom: '28%', left: '50%', transform: 'translateX(-50%)', fontSize: 96, opacity: 0.07 }}>✂️</div>
        </div>
      )}

      {/* Gradient overlay — bottom 62% */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, height: '62%',
        background: 'linear-gradient(to top,rgba(22,14,8,0.9) 0%,rgba(22,14,8,0.44) 52%,transparent 100%)',
      }} />

      {/* Right stripe accent */}
      <div style={{ position: 'absolute', top: 0, right: 0,  width: 7, height: '100%', background: t.a }} />
      <div style={{ position: 'absolute', top: 0, right: 7,  width: 2, height: '100%', background: t.aB }} />

      {/* Title block */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 9, padding: `0 ${M}px 32px`, boxSizing: 'border-box' }}>
        {pattern?.subtitle && (
          <p style={{ color: 'rgba(255,255,255,0.62)', fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.22em', fontFamily: t.bdy, margin: '0 0 9px 0' }}>
            {pattern.subtitle}
          </p>
        )}
        <h1 style={{ color: '#fff', fontSize: 34, fontWeight: 700, lineHeight: 1.15, fontFamily: t.h, margin: '0 0 12px 0', maxWidth: 460 }}>
          {pattern?.title || 'Crochet Pattern'}
        </h1>
        <div style={{ height: 1, width: 52, background: 'rgba(255,255,255,0.33)', margin: '0 0 12px 0' }} />
        {/* Stats pills */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {pattern?.difficulty && (
            <div style={{
              background: 'rgba(255,255,255,0.13)', border: '1px solid rgba(255,255,255,0.25)',
              borderRadius: 20, padding: '3px 12px',
              color: '#fff', fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: t.bdy,
            }}>
              {pattern.difficulty}
            </div>
          )}
          {pattern?.finished_size && (
            <span style={{ color: 'rgba(255,255,255,0.78)', fontSize: 10, fontFamily: t.bdy }}>✦ {pattern.finished_size}</span>
          )}
          {pattern?.language && (
            <span style={{ color: 'rgba(255,255,255,0.62)', fontSize: 10, fontFamily: t.bdy }}>✦ {pattern.language.toUpperCase()} Terms</span>
          )}
        </div>
        {/* Branding row */}
        {branding?.shop_name && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 18, paddingTop: 14, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
            {branding.logo_url && (
              <img src={branding.logo_url} alt="" crossOrigin="anonymous"
                   style={{ width: 22, height: 22, objectFit: 'contain', borderRadius: 4 }} />
            )}
            <span style={{ color: 'rgba(255,255,255,0.88)', fontSize: 10.5, fontWeight: 600, fontFamily: t.bdy }}>{branding.shop_name}</span>
            {branding.instagram && <span style={{ color: 'rgba(255,255,255,0.48)', fontSize: 9, fontFamily: t.bdy }}>@{branding.instagram}</span>}
          </div>
        )}
      </div>
      {/* Top left watermark */}
      <div style={{ position: 'absolute', top: 16, left: M, fontSize: 7.5, color: 'rgba(255,255,255,0.24)', fontFamily: t.bdy, letterSpacing: '0.14em', textTransform: 'uppercase' }}>
        {branding?.shop_name || 'Crochet Pattern'} · Digital Download
      </div>
    </Pg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PAGE 2 — INTRO / ABOUT
// ─────────────────────────────────────────────────────────────────────────────
export function IntroPage({ pattern, branding, pg }) {
  const t  = T(branding);
  const lv = { beginner: 1, easy: 2, intermediate: 3, advanced: 4 }[pattern?.difficulty] || 1;

  return (
    <Pg t={t}>
      <Hdr t={t} title={pattern?.title} shop={branding?.shop_name} pg={pg} />
      <Ftr t={t} copy={branding?.copyright} shop={branding?.shop_name} ig={branding?.instagram} />
      <Body>
        <SecTitle t={t} label="About This Pattern" />

        {/* Description text */}
        {pattern?.description && (
          <p style={{ fontSize: 11, color: '#374151', lineHeight: 1.8, fontFamily: t.bdy, margin: '0 0 18px 0', maxWidth: CW }}>
            {pattern.description}
          </p>
        )}

        {/* Skill level dots */}
        <div style={{ marginBottom: 18 }}>
          <p style={{ fontSize: 8, fontWeight: 700, color: t.a, textTransform: 'uppercase', letterSpacing: '0.13em', fontFamily: t.bdy, margin: '0 0 7px 0' }}>
            Skill Level
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            {[1, 2, 3, 4].map(i => (
              <div key={i} style={{
                width: 16, height: 16, borderRadius: '50%',
                background: i <= lv ? t.a : '#E5E7EB',
                border: `2.5px solid ${i <= lv ? t.a : '#E5E7EB'}`,
              }} />
            ))}
            <span style={{ fontSize: 11, color: '#6B7280', fontFamily: t.bdy, marginLeft: 10, textTransform: 'capitalize' }}>
              {pattern?.difficulty || 'Beginner'}
            </span>
          </div>
        </div>

        {/* Stats grid */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 2, background: t.aM, borderRadius: 10, overflow: 'hidden',
          marginBottom: 20, border: `1.5px solid ${t.aM}`,
        }}>
          {[
            { label: 'Finished Size', val: pattern?.finished_size || '—' },
            { label: 'Language',      val: pattern?.language === 'uk' ? 'UK Terms' : 'US Terms' },
            { label: 'Difficulty',    val: (pattern?.difficulty || 'Beginner').replace(/^\w/, c => c.toUpperCase()) },
            { label: 'Materials',     val: `${(pattern?.materials || []).length} items` },
          ].map(s => (
            <div key={s.label} style={{ background: '#fff', padding: '12px 8px', textAlign: 'center' }}>
              <p style={{ fontSize: 13, fontWeight: 700, color: t.a, fontFamily: t.h, margin: '0 0 3px 0', lineHeight: 1.2 }}>{s.val}</p>
              <p style={{ fontSize: 7.5, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.09em', margin: 0, fontFamily: t.bdy }}>{s.label}</p>
            </div>
          ))}
        </div>

        {/* Pattern notes or default */}
        <div style={{ background: t.aL, border: `1px solid ${t.aM}`, borderRadius: 9, padding: '14px 18px' }}>
          <p style={{ fontSize: 8, fontWeight: 700, color: t.a, textTransform: 'uppercase', letterSpacing: '0.13em', fontFamily: t.bdy, margin: '0 0 8px 0' }}>
            {pattern?.pattern_notes ? 'Pattern Notes' : 'Before You Begin'}
          </p>
          {pattern?.pattern_notes ? (
            <p style={{ fontSize: 11, color: '#374151', lineHeight: 1.75, fontFamily: t.bdy, margin: 0 }}>{pattern.pattern_notes}</p>
          ) : (
            <ul style={{ paddingLeft: 18, margin: 0 }}>
              {[
                `Written in ${pattern?.language === 'uk' ? 'UK' : 'US'} crochet terms throughout.`,
                'Work in continuous rounds unless otherwise noted.',
                'Use a stitch marker to mark the beginning of each round.',
                'Stuff firmly as you go before closing each piece.',
                'Leave a long tail when fastening off for sewing pieces together.',
              ].map((n, i) => (
                <li key={i} style={{ fontSize: 10.5, color: '#374151', lineHeight: 1.72, fontFamily: t.bdy }}>{n}</li>
              ))}
            </ul>
          )}
        </div>
      </Body>
    </Pg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PAGE 3 — MATERIALS
// ─────────────────────────────────────────────────────────────────────────────
export function MaterialsPage({ pattern, branding, pg }) {
  const t    = T(branding);
  const mats = pattern?.materials || [];
  const cats = [
    { key: 'yarn',       icon: '🧶', label: 'YARN' },
    { key: 'hook',       icon: '🪝', label: 'HOOK & NEEDLE' },
    { key: 'safety_eye', icon: '👁',  label: 'SAFETY EYES' },
    { key: 'stuffing',   icon: '☁️', label: 'STUFFING' },
    { key: 'notion',     icon: '🧵', label: 'NOTIONS & TOOLS' },
  ].map(c => ({ ...c, items: mats.filter(m => m.category === c.key) }))
   .filter(c => c.items.length > 0);

  return (
    <Pg t={t}>
      <Hdr t={t} title={pattern?.title} shop={branding?.shop_name} pg={pg} />
      <Ftr t={t} copy={branding?.copyright} shop={branding?.shop_name} ig={branding?.instagram} />
      <Body>
        <SecTitle t={t} label="You Will Need" />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
          {cats.map(cat => (
            <div key={cat.key}>
              {/* Category header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, paddingBottom: 6, marginBottom: 7, borderBottom: `2px solid ${t.aM}` }}>
                <span style={{ fontSize: 14, lineHeight: 1 }}>{cat.icon}</span>
                <span style={{ fontSize: 8.5, fontWeight: 700, color: t.a, textTransform: 'uppercase', letterSpacing: '0.13em', fontFamily: t.bdy }}>{cat.label}</span>
              </div>
              {cat.items.map((item, idx) => (
                <div key={item.id} style={{
                  display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
                  padding: '5px 10px',
                  background: idx % 2 === 0 ? '#FAFAFA' : '#fff',
                  borderBottom: `1px solid ${t.aL}`,
                }}>
                  <span style={{ fontSize: 11, color: '#1F2937', fontFamily: t.bdy, flex: 1 }}>{item.name}</span>
                  {item.amount && (
                    <span style={{ fontSize: 10.5, fontWeight: 700, color: t.a, fontFamily: t.bdy, marginLeft: 12, flexShrink: 0 }}>{item.amount}</span>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
        {/* Finished measurements box */}
        {(pattern?.finished_size || pattern?.gauge) && (
          <div style={{ marginTop: 18, background: t.aL, border: `1px solid ${t.aM}`, borderRadius: 9, padding: '12px 18px' }}>
            {pattern.finished_size && (
              <div style={{ marginBottom: pattern.gauge ? 8 : 0 }}>
                <p style={{ fontSize: 8, fontWeight: 700, color: t.a, textTransform: 'uppercase', letterSpacing: '0.12em', fontFamily: t.bdy, margin: '0 0 3px 0' }}>Finished Measurements</p>
                <p style={{ fontSize: 13, color: '#111827', fontFamily: t.h, fontWeight: 600, margin: 0 }}>{pattern.finished_size}</p>
              </div>
            )}
            {pattern.gauge && (
              <div>
                <p style={{ fontSize: 8, fontWeight: 700, color: t.a, textTransform: 'uppercase', letterSpacing: '0.12em', fontFamily: t.bdy, margin: '0 0 3px 0' }}>Gauge / Tension</p>
                <p style={{ fontSize: 11, color: '#374151', fontFamily: t.bdy, margin: 0 }}>{pattern.gauge}</p>
              </div>
            )}
          </div>
        )}
      </Body>
    </Pg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PAGE 4 — ABBREVIATIONS
// ─────────────────────────────────────────────────────────────────────────────
export function AbbreviationsPage({ pattern, branding, pg }) {
  const t    = T(branding);
  const sel  = (pattern?.abbreviations || []).filter(a => a.selected);
  if (!sel.length) return null;

  return (
    <Pg t={t}>
      <Hdr t={t} title={pattern?.title} shop={branding?.shop_name} pg={pg} />
      <Ftr t={t} copy={branding?.copyright} shop={branding?.shop_name} ig={branding?.instagram} />
      <Body>
        <SecTitle t={t} label="Abbreviations" />
        <p style={{ fontSize: 10, color: '#6B7280', fontStyle: 'italic', fontFamily: t.bdy, margin: '0 0 16px 0' }}>
          All abbreviations use {pattern?.language === 'uk' ? 'UK' : 'US'} crochet terms throughout this pattern.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px 32px' }}>
          {sel.map((a, i) => (
            <div key={a.id} style={{
              display: 'flex', gap: 10, alignItems: 'baseline',
              padding: '6px 8px', borderBottom: `1px solid ${t.aL}`,
              background: i % 2 === 0 ? '#F9FAFB' : '#fff',
              borderRadius: 3,
            }}>
              <span style={{ fontFamily: t.mo, fontSize: 11.5, fontWeight: 700, color: t.a, minWidth: 50, flexShrink: 0 }}>{a.code}</span>
              <span style={{ fontSize: 10.5, color: '#374151', lineHeight: 1.5, fontFamily: t.bdy }}>{a.definition}</span>
            </div>
          ))}
        </div>
      </Body>
    </Pg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PAGES 5+ — PATTERN SECTIONS
// ─────────────────────────────────────────────────────────────────────────────
export function SectionPage({ section, pattern, branding, pg }) {
  const t  = T(branding);
  const sa = section.color || t.a;
  const { r, g, b } = hexRgb(sa);
  const saL = `rgba(${r},${g},${b},0.08)`;
  const saM = `rgba(${r},${g},${b},0.20)`;

  const regularCount = section.rounds.filter(rnd => ['round', 'row'].includes(rnd.type)).length;

  const CALLOUT = {
    note:     { bg: '#EFF6FF', border: '#93C5FD', icon: '📝', tc: '#1D4ED8' },
    tip:      { bg: '#FFFBEB', border: '#FCD34D', icon: '💡', tc: '#92400E' },
    warning:  { bg: '#FEF2F2', border: '#FCA5A5', icon: '⚠️', tc: '#B91C1C' },
    assembly: { bg: '#F0FDF4', border: '#6EE7B7', icon: '🧩', tc: '#065F46' },
  };

  return (
    <Pg t={t}>
      <Hdr t={t} title={pattern?.title} shop={branding?.shop_name} pg={pg} />
      <Ftr t={t} copy={branding?.copyright} shop={branding?.shop_name} ig={branding?.instagram} />
      <Body>
        {/* Section title bar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <div style={{ width: 5, height: 24, background: sa, borderRadius: 3, flexShrink: 0 }} />
          <h2 style={{ fontSize: 17, fontWeight: 700, color: '#111827', fontFamily: t.h, flex: 1, margin: 0 }}>
            {section.title}
          </h2>
          {regularCount > 0 && (
            <div style={{ background: saL, border: `1px solid ${saM}`, borderRadius: 5, padding: '3px 9px' }}>
              <span style={{ fontSize: 8.5, fontWeight: 700, color: sa, fontFamily: t.bdy }}>{regularCount} round{regularCount !== 1 ? 's' : ''}</span>
            </div>
          )}
        </div>

        {section.notes && (
          <p style={{ fontSize: 10, color: '#6B7280', fontStyle: 'italic', fontFamily: t.bdy, marginBottom: 10 }}>{section.notes}</p>
        )}

        {/* Rounds */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {section.rounds.map((rnd, i) => {
            const isSpec = ['note', 'tip', 'warning', 'assembly'].includes(rnd.type);

            if (isSpec) {
              const cfg = CALLOUT[rnd.type] || CALLOUT.note;
              return (
                <div key={rnd.id} style={{
                  background: cfg.bg, border: `1px solid ${cfg.border}`,
                  borderRadius: 7, padding: '9px 12px',
                  display: 'flex', gap: 9, alignItems: 'flex-start',
                  margin: '5px 0',
                }}>
                  <span style={{ fontSize: 14, flexShrink: 0, lineHeight: 1.3 }}>{cfg.icon}</span>
                  <span style={{ fontSize: 10.5, color: cfg.tc, lineHeight: 1.6, fontFamily: t.bdy }}>{rnd.instruction}</span>
                </div>
              );
            }

            return (
              <div key={rnd.id} style={{
                display: 'flex', alignItems: 'stretch',
                borderBottom: `1px solid ${saL}`,
                background: i % 2 === 0 ? '#FAFAFA' : '#fff',
                minHeight: 32,
              }}>
                {/* Round label badge */}
                <div style={{
                  width: 62, flexShrink: 0, background: sa,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: '5px 4px',
                }}>
                  <span style={{ fontSize: 8, fontWeight: 700, color: '#fff', fontFamily: t.bdy, textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.04em', lineHeight: 1.3 }}>
                    {rnd.label || `R${i + 1}`}
                  </span>
                </div>

                {/* Instruction */}
                <div style={{ flex: 1, padding: '7px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontSize: 11, color: '#1F2937', lineHeight: 1.55, fontFamily: t.bdy, flex: 1 }}>
                    {rnd.instruction || '—'}
                  </span>
                  {rnd.stitch_count && (
                    <div style={{ flexShrink: 0, background: saL, border: `1px solid ${saM}`, borderRadius: 5, padding: '2px 8px' }}>
                      <span style={{ fontSize: 10.5, fontWeight: 700, color: sa, fontFamily: t.mo }}>({rnd.stitch_count})</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Body>
    </Pg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// GALLERY PAGE — finished / assembly photos
// ─────────────────────────────────────────────────────────────────────────────
export function GalleryPage({ pattern, photos, branding, pg }) {
  const t   = T(branding);
  const pp  = photos || [];
  const gallery = [
    ...pp.filter(p => p.category === 'assembly'),
    ...pp.filter(p => p.category === 'cover').slice(1),
  ].slice(0, 3);
  if (!gallery.length) return null;

  const cols = gallery.length === 1 ? '1fr' : gallery.length === 2 ? '1fr 1fr' : 'repeat(3, 1fr)';
  const h    = gallery.length === 1 ? 560 : 340;

  return (
    <Pg t={t}>
      <Hdr t={t} title={pattern?.title} shop={branding?.shop_name} pg={pg} />
      <Ftr t={t} copy={branding?.copyright} shop={branding?.shop_name} ig={branding?.instagram} />
      <Body>
        <SecTitle t={t} label="Finished Project" />
        <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 10, height: h }}>
          {gallery.map(p => (
            <div key={p.id} style={{ borderRadius: 10, overflow: 'hidden', height: '100%' }}>
              <img src={p.url} alt={p.caption || ''} crossOrigin="anonymous"
                   style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
          ))}
        </div>
      </Body>
    </Pg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BACK PAGE — thank you + branding
// ─────────────────────────────────────────────────────────────────────────────
export function BackPage({ pattern, branding }) {
  const t = T(branding);
  return (
    <Pg t={t} bg="#FAF7F4">
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 7, background: t.a }} />
      <div style={{ position: 'absolute', top: 7, left: 0, right: 0, height: 2, background: t.aB }} />

      <div style={{
        position: 'absolute', top: 9, bottom: 0, left: 0, right: 0,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        padding: `0 ${M * 1.5}px`, textAlign: 'center', boxSizing: 'border-box',
      }}>
        {branding?.logo_url && (
          <img src={branding.logo_url} alt="" crossOrigin="anonymous"
               style={{ width: 54, height: 54, objectFit: 'contain', borderRadius: 8, marginBottom: 14 }} />
        )}
        <h2 style={{ fontSize: 28, fontWeight: 700, color: t.a, fontFamily: t.h, margin: '0 0 10px 0' }}>Thank You!</h2>
        <p style={{ fontSize: 12, color: '#6B7280', lineHeight: 1.78, fontFamily: t.bdy, maxWidth: 360, margin: '0 0 28px 0' }}>
          Thank you so much for purchasing this pattern. I hope you enjoy making it!
          I'd love to see your finished project — please tag me online.
        </p>

        {/* Shop card */}
        <div style={{ background: '#fff', border: `1.5px solid ${t.aM}`, borderRadius: 14, padding: '20px 32px', minWidth: 280, boxShadow: '0 3px 16px rgba(0,0,0,0.06)' }}>
          {branding?.shop_name && (
            <p style={{ fontSize: 16, fontWeight: 700, color: '#1F2937', fontFamily: t.h, margin: '0 0 10px 0' }}>{branding.shop_name}</p>
          )}
          {branding?.instagram && (
            <p style={{ fontSize: 11, color: '#6B7280', fontFamily: t.bdy, margin: '0 0 4px 0' }}>Instagram: @{branding.instagram}</p>
          )}
          {branding?.website && (
            <p style={{ fontSize: 11, color: '#6B7280', fontFamily: t.bdy, margin: '0 0 4px 0' }}>{branding.website}</p>
          )}
        </div>

        <p style={{ fontSize: 9, color: '#9CA3AF', fontFamily: t.bdy, marginTop: 22, lineHeight: 1.65, maxWidth: 380 }}>
          {branding?.copyright || `© ${new Date().getFullYear()} ${branding?.shop_name || 'Designer'}. All rights reserved.`}
          {' '}This pattern is for personal use only. Please do not redistribute, resell, or share without permission.
        </p>
      </div>

      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 4, background: t.aM }} />
    </Pg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ASSEMBLER — builds the full ordered page array
// ─────────────────────────────────────────────────────────────────────────────
export function PatternPages({ pattern, photos = [], branding = {} }) {
  if (!pattern) return [];

  const pp = photos || [];
  const b  = branding || {};
  const pages = [];
  let n = 1;

  // 1. Cover (no page number shown)
  pages.push(<CoverPage key="cover" pattern={pattern} photos={pp} branding={b} />);

  // 2. Intro (always — even if description is empty, shows defaults)
  pages.push(<IntroPage key="intro" pattern={pattern} branding={b} pg={n++} />);

  // 3. Materials
  if ((pattern.materials || []).length > 0) {
    pages.push(<MaterialsPage key="mats" pattern={pattern} branding={b} pg={n++} />);
  }

  // 4. Abbreviations
  if ((pattern.abbreviations || []).some(a => a.selected)) {
    pages.push(<AbbreviationsPage key="abbrs" pattern={pattern} branding={b} pg={n++} />);
  }

  // 5. One page per section
  (pattern.sections || []).forEach((sec, i) => {
    pages.push(<SectionPage key={`s${i}`} section={sec} pattern={pattern} branding={b} pg={n++} />);
  });

  // 6. Gallery (assembly photos or extra cover photos)
  const hasGallery =
    pp.some(p => p.category === 'assembly') ||
    pp.filter(p => p.category === 'cover').length > 1;
  if (hasGallery) {
    pages.push(<GalleryPage key="gallery" pattern={pattern} photos={pp} branding={b} pg={n++} />);
  }

  // 7. Back page (always)
  pages.push(<BackPage key="back" pattern={pattern} branding={b} />);

  return pages;
}
